import { toast } from "@ui/molecules/Toasts/toast";

type ShareURLOptions = {
	url?: string;
	title?: string;
	text?: string;
	copiedTitle?: string;
	copiedDescription?: string;
	copyErrorTitle?: string;
	copyErrorDescription?: string;
};

const currentPageURL = () => window.location.href;

const shareDataFromOptions = (options: ShareURLOptions): ShareData => ({
	...(options.title ? { title: options.title } : {}),
	...(options.text ? { text: options.text } : {}),
	url: options.url ?? currentPageURL(),
});

export async function tryShareURL(options: ShareURLOptions): Promise<boolean> {
	const shareData = shareDataFromOptions(options);

	if (
		typeof navigator.share !== "function" ||
		(typeof navigator.canShare === "function" && !navigator.canShare(shareData))
	) {
		return false;
	}

	try {
		await navigator.share(shareData);
		return true;
	} catch (error) {
		if (error instanceof DOMException && error.name === "AbortError") {
			return true;
		}
		return false;
	}
}

/** Copies the URL to the clipboard directly, skipping the native share sheet entirely. */
export async function copyURLToClipboard(options: ShareURLOptions): Promise<void> {
	const shareData = shareDataFromOptions(options);

	try {
		await navigator.clipboard.writeText(shareData.url ?? currentPageURL());
		toast.success({
			title: options.copiedTitle ?? "Link copied",
			description:
				options.copiedDescription ??
				"You can paste it into a chat, email, or document.",
		});
	} catch {
		toast.error({
			title: options.copyErrorTitle ?? "Could not copy",
			description:
				options.copyErrorDescription ??
				"Copy the address from your browser's address bar, or allow clipboard access for this site.",
		});
	}
}

export async function shareURL(options: ShareURLOptions): Promise<void> {
	const shared = await tryShareURL(options);
	if (shared) {
		return;
	}

	await copyURLToClipboard(options);
}

const NATIVE_APP_SCHEMES = /^(mailto|sms|tel):/i;

/**
 * Opens a share destination. `mailto:` and `sms:` must not use `window.open` — most
 * browsers ignore or block that; a transient `<a click>` hands off to the OS handler.
 */
export function openShareDestination(href: string): void {
	if (NATIVE_APP_SCHEMES.test(href)) {
		const anchor = document.createElement("a");
		anchor.href = href;
		anchor.rel = "noopener noreferrer";
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
		return;
	}

	window.open(href, "_blank", "noopener,noreferrer");
}

/** Delay before opening a tab so a copy toast can be read first (copy-then-open flows). */
export const SHARE_DESTINATION_DELAY_MS = 3_000;

export function openShareDestinationAfterDelay(
	href: string,
	delayMs = SHARE_DESTINATION_DELAY_MS,
): void {
	window.setTimeout(() => openShareDestination(href), delayMs);
}
